import { RootState } from "../store";
import { FormSelectors } from "../store/types";

import { AutocompleteProps } from "./Autocomplete";
import { CheckboxProps } from "./Checkbox";
import { CopyProps } from "./Copy";
import { DatePickerProps } from "./DatePicker";
import {
  MultiImageUploaderProps,
  SingleImageUploaderProps,
} from "./ImageUploader";
import { InputProps } from "./Input";
import { MarketingCheckboxProps } from "./MarketingCheckbox";
import { MultiplePersonProps } from "./MultiplePerson";
import { PhoneProps } from "./Phone";
import { RadioProps } from "./Radio";
import { SectionProps } from "./Section";
import { SelectProps } from "./Select";
import { SelectCityProps } from "./SelectCity";
import { SelectMakeProps } from "./SelectMake/SelectMake";
import { SelectModelProps } from "./SelectModel/SelectModel";
import { TextareaProps } from "./Textarea";
import { TimePickerProps } from "./TimePicker";
import { ZipCodeProps } from "./ZipCode";
import { FormElement, FormId } from "./enums";

type SubtractCommonProps<
  ComponentProps,
  FormProps = CommonFormElProps
> = Exclude<keyof ComponentProps, keyof FormProps>;

export type DistributiveOmit<T, K extends keyof any> = T extends any
  ? Omit<T, K>
  : never;

type IncludeProps = keyof Pick<
  CommonFormElProps,
  "id" | "isComplex" | "defaultValue" | "label" | "onChange"
>;

type FormContextProps = "control" | "register" | "setValue" | "trigger";

export type AutocompleteComponent = {
  readonly type: FormElement.AUTOCOMPLETE;
} & AutocompleteProps;

export type CheckboxComponent = {
  readonly type: FormElement.CHECKBOX;
} & CheckboxProps;

export type CopyComponent = {
  readonly type: FormElement.COPY;
} & CopyProps;

export type DatePickerComponent = {
  readonly type: FormElement.DATE_PICKER;
} & DatePickerProps;

export type InputComponent = {
  readonly type: FormElement.INPUT | FormElement.NUMBER;
} & InputProps;

export type MarketingCheckboxComponent = {
  readonly type: FormElement.MARKETING_CHECKBOX;
} & MarketingCheckboxProps;

export type MultiImageUploaderComponent = {
  readonly type: FormElement.IMAGE_UPLOADER_MULTIPLE;
} & MultiImageUploaderProps;

export type MultiplePersonComponent = {
  readonly type: FormElement.MULTIPLE_PERSON;
} & MultiplePersonProps;

export type PhoneComponent = {
  readonly type: FormElement.PHONE;
} & PhoneProps;

export type RadioComponent = {
  readonly type: FormElement.RADIO;
} & RadioProps;

export type SelectComponent = {
  readonly type: FormElement.SELECT;
} & SelectProps;

export type SelectCityComponent = {
  readonly type: FormElement.SELECT_CITY;
} & SelectCityProps;

export type SelectMakeComponent = {
  readonly type: FormElement.SELECT_MAKE;
} & SelectMakeProps;

export type SelectModelComponent = {
  readonly type: FormElement.SELECT_MODEL;
} & SelectModelProps;

export type SingleImageUploaderComponent = {
  readonly type: FormElement.IMAGE_UPLOADER_SINGLE;
} & SingleImageUploaderProps;

export type TextareaComponent = {
  readonly type: FormElement.TEXTAREA;
} & TextareaProps;

export type TimePickerComponent = {
  readonly type: FormElement.TIME_PICKER;
} & TimePickerProps;

export type ZipCodeComponent = {
  readonly type: FormElement.ZIP_CODE;
} & ZipCodeProps;

export type CommonFormElProps = {
  readonly id: string;
  readonly label: string;
  readonly defaultValue?: unknown;
  readonly disabled?: boolean;
  readonly hasError?: boolean;
  readonly required?: boolean;
  readonly onChange?: any;
  readonly isComplex?: boolean;
};
type FormSchemaComponentsPropsOverride = {
  readonly disabled?: boolean | ((store: RootState) => boolean);
  readonly required?: boolean | ((store: RootState) => boolean);
  readonly isVisible?: boolean | ((store: RootState) => boolean);
};

export type SimpleFormComponents =
  | AutocompleteComponent
  | CopyComponent
  | DatePickerComponent
  | InputComponent
  | MarketingCheckboxComponent
  | PhoneComponent
  | RadioComponent
  | SelectComponent
  | SelectCityComponent
  | SelectMakeComponent
  | SelectModelComponent
  | TextareaComponent
  | TimePickerComponent
  | ZipCodeComponent;

export type SimpleFormSchemaComponents =
  | (Pick<
      AutocompleteComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<AutocompleteProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      CheckboxComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<CheckboxProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      CopyComponent,
      | "type"
      | Exclude<IncludeProps, "label">
      | Exclude<
          SubtractCommonProps<CopyProps>,
          FormContextProps & Pick<CopyProps, "content">
        >
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      DatePickerComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<DatePickerProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      InputComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<InputProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      MarketingCheckboxComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<MarketingCheckboxProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      PhoneComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<PhoneProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      RadioComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<RadioProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      SelectComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<SelectProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      SelectCityComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<SelectCityProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      SelectMakeComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<SelectMakeProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      SelectModelComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<SelectModelProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      TextareaComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<TextareaProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      TimePickerComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<TimePickerProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride)
  | (Pick<
      ZipCodeComponent,
      | "type"
      | IncludeProps
      | Exclude<SubtractCommonProps<ZipCodeProps>, FormContextProps>
    > &
      FormSchemaComponentsPropsOverride);

export type ComplexFormSchemaComponents =
  | (Omit<MultiplePersonComponent, "register"> &
      FormSchemaComponentsPropsOverride)
  | (Omit<
      SingleImageUploaderComponent,
      "allowedExtensions" | "onSelectFile" | "setFile" | "image"
    > &
      FormSchemaComponentsPropsOverride)
  | (MultiImageUploaderComponent & FormSchemaComponentsPropsOverride);

export type IsVisible = (state: RootState, sectionId: string) => boolean;

export type FormSchema<C> = {
  readonly content: Array<FormSections<C>>;
  readonly selector: FormSelectors;
  readonly formId?: FormId;
  readonly id?: string;
  readonly isVisible?: IsVisible;
  readonly redirect?: ((state: RootState) => string | null) | string;
};

export type FormSections<C> = {
  readonly sections: Array<SectionProps<C>>;
  readonly title?: string;
};

export const ComplexComponentsArray = [
  FormElement.IMAGE_UPLOADER_SINGLE,
  FormElement.IMAGE_UPLOADER_MULTIPLE,
  FormElement.MULTIPLE_PERSON,
];

export function isComplexComponent(
  component: ComplexFormSchemaComponents | SimpleFormSchemaComponents
): component is ComplexFormSchemaComponents {
  return component.isComplex === true;
}
